import { motion } from "framer-motion";
import TypewriterHeading from "./TypewriterHeading";

const aboutItems = [
  {
    icon: "⚡",
    text: "Seasoned software developer passionate about functional programming and JavaScript.",
  },
  {
    icon: "🤖",
    text: "Deep interests in AI/ML, blockchain, smart contracts, Web3, cryptocurrencies and the Metaverse.",
  },
  {
    icon: "🎨",
    text: "Skilled in front-end technologies: HTML5, CSS3, JSON, REST APIs and modern CSS frameworks.",
  },
  {
    icon: "📱",
    text: "Proficient in React, React Native, Angular, OOJS, jQuery and ES6+.",
  },
  {
    icon: "🛠",
    text: "Experience with Bootstrap and AJAX for responsive, accessible UI development.",
  },
  {
    icon: "🐍",
    text: "Working knowledge of Python fundamentals and scripting.",
  },
  {
    icon: "🚀",
    text: "Backend expertise with Node.js and Express.js for scalable server-side applications.",
  },
  { icon: "🗄", text: "Experience with databases: MongoDB and MySQL." },
];

const skills = [
  { name: "React", xp: 95, level: "LEGENDARY" },
  { name: "React Native", xp: 95, level: "LEGENDARY" },
  { name: "TypeScript", xp: 88, level: "LEGENDARY" },
  { name: "Angular", xp: 80, level: "EPIC" },
  { name: "Node.js", xp: 85, level: "LEGENDARY" },
  { name: "GraphQL", xp: 75, level: "EPIC" },
  { name: "MongoDB", xp: 70, level: "EPIC" },
  { name: "REST", xp: 90, level: "LEGENDARY" },
  { name: "Git", xp: 92, level: "LEGENDARY" },
  { name: "Jest", xp: 78, level: "EPIC" },
  { name: "Webpack", xp: 72, level: "EPIC" },
  { name: "Tailwind", xp: 85, level: "LEGENDARY" },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0 },
};

const HackerAboutMe = () => {
  return (
    <div className="space-y-6">
      {/* About card */}
      <div className="glass-card rounded-2xl p-6 relative overflow-hidden">
        {/* Subtle top accent line */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, #00ff99, #06b6d4, transparent)",
          }}
        />

        <TypewriterHeading text="$ cat ~/about_me.md" />

        <motion.ul
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-1"
        >
          {aboutItems.map((item, index) => (
            <motion.li
              key={index}
              variants={itemVariants}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="about-item group"
            >
              <span className="text-base mt-0.5 flex-shrink-0">
                {item.icon}
              </span>
              <span className="text-sm text-slate-300/85 group-hover:text-slate-100 transition-colors duration-300 leading-relaxed">
                {item.text}
              </span>
            </motion.li>
          ))}
        </motion.ul>
      </div>

      {/* Skills grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.6 }}
        className="glass-card rounded-2xl p-6 relative overflow-hidden"
      >
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, #06b6d4, #a855f7, transparent)",
          }}
        />
        <h3 className="text-sm tracking-[0.15em] text-slate-200/90 mb-5 font-mono">
          <span className="text-cyan-400/60">$</span> cat ~/skills/inventory.yml
        </h3>
        <div className="space-y-3">
          {skills.map((skill, i) => (
            <motion.div
              key={skill.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1 + i * 0.05 }}
              className="group"
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-slate-200/90">
                    {skill.name}
                  </span>
                  <span
                    className={`text-[9px] px-1.5 py-0.5 rounded font-bold tracking-wider ${
                      skill.level === "LEGENDARY"
                        ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                        : "bg-purple-500/15 text-purple-300 border border-purple-500/30"
                    }`}
                  >
                    {skill.level}
                  </span>
                </div>
                <span className="text-[10px] text-slate-400/70 font-mono">
                  {skill.xp}%
                </span>
              </div>
              <div className="h-1.5 bg-slate-800/50 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${skill.xp}%` }}
                  transition={{
                    delay: 1.2 + i * 0.05,
                    duration: 0.8,
                    ease: "easeOut",
                  }}
                  className={`h-full rounded-full ${
                    skill.level === "LEGENDARY"
                      ? "bg-gradient-to-r from-amber-500/60 to-orange-500/60"
                      : "bg-gradient-to-r from-purple-500/60 to-pink-500/60"
                  }`}
                  style={{
                    boxShadow:
                      skill.level === "LEGENDARY"
                        ? "0 0 8px rgba(251,191,36,0.4)"
                        : "0 0 8px rgba(168,85,247,0.4)",
                  }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default HackerAboutMe;
